import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import PosJournalFilter from './PosJournalFilter';
import { isPermissionCodeValid } from '../../../utils/mics';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import moment from 'moment';

const PosJournalList = ({ pageId }) => {

    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`)
    

    let navigate = useNavigate();
    const dispatch = useDispatch();
    const posJournalList = useSelector(
        (state) => state.posJournal.posjournalList,
    );

    const posJournal = useSelector((state) => state.posJournal.posjournal);
    const filters = useSelector((state) => state.posJournal.filters);
    
    const [visible, setVisible] = useState(false);

    const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
        // date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        // date_till: moment().format('YYYY-MM-DD'),
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
    
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [deleteRecord, setDeleteRecord] = useState(null);

    useEffect(() => {
        if (posJournal.id) {
            setVisible(true);
        }
    }, [posJournal]);

    const handleRefresh = useCallback(() => {
        dispatch(appActions.POSJOURNAL_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    const handleSearch = useCallback(
        (params) => {
            const searchParams = {
                ...params,
                ...formValues,
                pageno: params.pageno || defaultParams?.pageno,
                pagesize: params.pagesize || defaultParams?.pagesize,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };
    
            dispatch(appActions.POSJOURNAL_GET_ALL_REQUEST(searchParams));
        },
        [dispatch, defaultParams, formValues]  // Add dispatch and defaultParams to dependencies
    );

    useEffect(() => {
        handleSearch(defaultParams);
    }, []); // Empty dependency array ensures it runs only on mount
    
    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    const handleViewRedirect = useCallback((record) => {
        const data = posJournalList?.data?.find((t) => t.id === record.id);
        navigate('/ui/pos-journal/detail', {
            state: { data, isView: true },
        });
    }, [posJournalList, navigate]);

    const handleCloseDelete = () => {
        setVisible(false);
        dispatch(appActions.PROPCONFIG_RESET_REQUEST(defaultParams));
        dispatch(appActions.POSJOURNAL_GET_ALL_REQUEST(defaultParams));
    };

    const renderHeader = () => {
        return ( 
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex"> Pos Journal </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp'  data-pr-tooltip="Refresh" onClick={handleRefresh} />
            </div> 
        );
    };

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.POSJOURNAL_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])


    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                { readAccess && 
                    <span className="mr-2 cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                { deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
               }
            </div>
        ),
        [handleViewRedirect, readAccess],
    );
   
    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );

    const columns = [
        { field: 'fileId', header: 'FileId', sortable: true },
        { field: 'storeId', header: 'Store Id', sortable: true },
        { field: 'trxId', header: 'Trx Id', sortable: true },
        { field: 'terminalId', header: 'Terminal Id', sortable: true },
        { field: 'tlogExtApiError', header: 'TlogExt Api Error', sortable: true },
        { field: 'pjofilePath', header: 'Pjo File Path', sortable: true},
        { field: 'trxType', header: 'Trx Type', sortable: true },
        { field: 'trxDate', header: 'Trx Date', sortable: true },
        { field: 'trxTime', header: 'Trx Time', sortable: true },
        { field: 'pjoxmlStatus', header: 'Pjo Xml Status', sortable: true },
        { field: 'eodxmlStatus', header: 'Eod Xml Status', sortable: true },
        { field: 'error', header: 'Error', sortable: true },
        { field: 'status', header: 'Status', sortable: true },
        { field: 'createdOn', header: 'Created On', sortable: true, width: '140px', columnType: 'datetime' },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, width : '150px', columnType : 'datetime' },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true, width : '150px' },
    ];

    const renderList = () => {
        return (
            <>
                <div className="grid px-4 align-items-center my-2"> {renderHeader()} </div>
                <div className="bg-white mx-4 pv-20 ph-20 br-6 shadow-white">
                    <PosJournalFilter
                        handleSearch={handleSearch}
                        handleReset={defaultParamsWithoutFilters}
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={posJournalList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='pos_journal'
                        isLazy={true}
                        showGenerateXmlBtn={true}
                        pageId={ pageId }
                    >
                    </PrimeTable>
                </div>
            </>
        );
    };
    
    return <div className="form_height">
        {renderList()}
        {deleteRecord && (
            <ConfirmationDialog
                message={`Are you sure you want to delete the Pos Journal Trx  : ${deleteRecord.trxId}?`}
                onAccept={handleDeleteConfirm}
                onReject={() => setDeleteRecord(null)}
            />
        )}
    </div>;
};

export default PosJournalList;
